import { HttpParams } from '@angular/common/http';
import { Injectable, inject } from '@angular/core';
import { ConfigurationService } from '@pflegix/config';
import { HttpClientService } from '@pflegix/http-client';
import type { CreateRatingDto, IRatingPopulated, IRatingStatsBusinessPartner, PaginateResultDto } from '@pflegix/types';
import { lastValueFrom, Observable } from 'rxjs';

enum RatingTopic {
  GET_RATING = 'GET_RATING',
  GET_RATING_POPULATED = 'GET_RATING_POPULATED',
  GET_RATINGS_PAGINATED = 'GET_RATINGS_PAGINATED',
  GET_RATING_STATISTICS_FOR_BUSINESSPARTNER = 'GET_RATING_STATISTICS_FOR_BUSINESSPARTNER',
}

@Injectable({ providedIn: 'root' })
export class RatingApi {
  private readonly config = inject(ConfigurationService);
  private readonly httpClient = inject(HttpClientService);

  private get apiUrl(): string {
    return `${this.config.get('basePflegixApiUrl')}/api/ratings`;
  }

  /**
   * Retrieves a paginated list of leads.
   *
   * @param page - The page number.
   * @param limit - The maximum number of leads per page.
   * @param sort - Optional. The sorting criteria for the leads.
   * @param filter - Optional. The filtering criteria for the leads.
   * @returns A Promise that resolves to a PaginateResultDto containing the paginated leads.
   */
  public getRatingsPaginated(
    page: number,
    limit: number,
    sort?: string,
    filter?: string
  ): Observable<PaginateResultDto<IRatingPopulated>> {
    let params = new HttpParams().set('page', page).set('limit', limit);

    if (sort) {
      params = params.set('sort', sort);
    }

    if (filter) {
      params = params.set('filter', filter);
    }

    params = params.set('own', 'false');

    return this.httpClient.get<PaginateResultDto<IRatingPopulated>>(
      this.apiUrl,
      this.httpClient.buildResilienceConfigForTopic(RatingTopic.GET_RATINGS_PAGINATED),
      { params }
    );
  }

  public getRating(ratingId: string): Promise<IRatingPopulated> {
    return lastValueFrom(
      this.httpClient.get<IRatingPopulated>(
        `${this.apiUrl}/${ratingId}`,
        this.httpClient.buildResilienceConfigForTopic(RatingTopic.GET_RATING_POPULATED)
      )
    );
  }

  public create(createRatingDto: CreateRatingDto): Observable<IRatingPopulated> {
    return this.httpClient.post<IRatingPopulated>(`${this.apiUrl}/`, createRatingDto);
  }

  public getRatingStatisticsForBusinesspartner(businessPartnerId: string): Promise<IRatingStatsBusinessPartner> {
    const params = new HttpParams().set('aggregate', 'both');

    return lastValueFrom(
      this.httpClient.get<IRatingStatsBusinessPartner>(
        `${this.apiUrl}/by-business-partner-aggregated/${businessPartnerId}`,
        this.httpClient.buildResilienceConfigForTopic(RatingTopic.GET_RATING_STATISTICS_FOR_BUSINESSPARTNER),
        { params }
      )
    );
  }
}
